import React, { useEffect, useState } from "react";
import ConfiguredVitalTable from "./configuredVitalTable";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Flex, Modal, Spin } from "antd";
import nodatafound from "../../../../../Assets/Icons/NoDataCard2.svg";
import {
  fetchRpmConfiguredVitalsList,
  setSelectedRpmPatient,
} from "../../../../../Redux/actions/rpmActions";
import { CiCirclePlus } from "react-icons/ci";
import VitalService from "../../../../../Services/VitalService";
import AddVitals from "./addConfigVitals";
import VitalTrends from "./vitalTrends";
import dayjs from "dayjs";
import WatchData from "./watchData";

const vitalStyleArray = [
  {
    title: "Blood Pressure",
    color: "#FB7800",
    backgroundColor: "#FFD5B8",
    icon: require("../../../../../Assets/Icons/Vital/Blood-Pressure.svg")
      .default,
    vitalCode: "BP",
    unit: "mmHg",
  },
  {
    title: "BMI",
    color: "#14BC57",
    backgroundColor: "#B5FFD2",
    icon: require("../../../../../Assets/Icons/Vital/Weight.svg").default,
    vitalCode: "BMI",
    unit: "Kg/m²",
  },
  {
    title: "Heart Rate",
    color: "#FC1D52",
    backgroundColor: "#FFCDD8",
    icon: require("../../../../../Assets/Icons/Vital/Heart-Rate.svg").default,
    vitalCode: "HR",
    unit: "bpm",
  },
  {
    title: "Respiratory Rate",
    color: "#FFB800",
    backgroundColor: "#FCFEA5",
    icon: require("../../../../../Assets/Icons/Vital/respiration.svg").default,
    vitalCode: "RR",
    unit: "bpm",
  },
  {
    title: "SpO2",
    color: "#6500F0",
    backgroundColor: "#E9DCFB",
    icon: require("../../../../../Assets/Icons/Vital/Oxygen.svg").default,
    vitalCode: "SPO2",
    unit: "%",
  },
  {
    title: "Temperature",
    color: "#4880DE",
    backgroundColor: "#B8DAFF",
    icon: require("../../../../../Assets/Icons/Vital/Temperature.svg").default,
    vitalCode: "TP",
    unit: "°F",
  },
  {
    title: "Weight",
    color: "#964B00",
    backgroundColor: "#EFD8C5",
    icon: require("../../../../../Assets/newicons/weight.svg").default,
    vitalCode: "WE",
    unit: "Kg",
  },
  {
    title: "Height",
    color: "#008080",
    backgroundColor: "#C1EFEF",
    icon: require("../../../../../Assets/newicons/height.svg").default,
    vitalCode: "HI",
    unit: "cm",
  },
];
const VITAL_ORDER = ["BP", "HR", "SPO2", "RR", "BMI", "WE", "HI", "TP", "BG"];

function Index() {
  const [currentPage, setCurrentPage] = useState(1);
  const [savedVitalsList, setSavedVitalsList] = useState([]);
  const [savedVitLoader, setSavedVitLoader] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [latestVitalData, setLatestVitalData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const { isLoading, rpmConfigVitalsList, selectedRpmPatient } = useSelector(
    (state) => state.rpm,
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (!selectedRpmPatient) {
      const rpmPatientJson = sessionStorage.getItem("rpmPatient");
      const rpmPatientObject = rpmPatientJson
        ? JSON.parse(rpmPatientJson)
        : null;
      if (rpmPatientObject) dispatch(setSelectedRpmPatient(rpmPatientObject));
    }
  }, []);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!selectedRpmPatient?.patientId) return;
      const payload = {
        patientId: selectedRpmPatient?.patientId,
        status: 1,
      };
      await Promise.all([
        dispatch(fetchRpmConfiguredVitalsList(payload)),
        fetchPatientVitals(selectedMonth),
        fetchLatestVital(),
      ]);
    };

    fetchAllData();
  }, [selectedRpmPatient?.patientId]);

  const VITAL_MAP = {
    BMI: "BMI",
    HR: "HR",
    BPS: "BPS",
    BPD: "BPD",
    WE: "WE",
    HI: "HI",
    SPO2: "SPO2",
    TP: "TP",
    RR: "RR",
  };

  const fetchPatientVitals = async (selectedMonth = dayjs()) => {
    const basePayload = {
      account_Id: selectedRpmPatient?.account_Id,
      patientId: selectedRpmPatient?.patientId,
      fromDate: dayjs(selectedMonth).startOf("month").format("MM/DD/YYYY"),
      toDate: dayjs(selectedMonth).endOf("month").format("MM/DD/YYYY"),
      status: 1,
    };

    setSavedVitalsList([]);
    setSavedVitLoader(true);

    try {
      const response = await VitalService.getVitalsData(basePayload);

      // create all detail API requests in parallel
      const detailRequests = (response.data || []).map((item) => {
        const detailPayload = {
          ...basePayload,
          fromDate: item.recordedDateTime,
          toDate: item.recordedDateTime,
        };

        return VitalService.getSpecificVitalData(detailPayload).then((res) => ({
          recordedDateTime: item.recordedDateTime,
          data: Array.isArray(res.data) ? res.data : [],
        }));
      });

      // run all API calls together
      const results = await Promise.all(detailRequests);

      const finalVitalArray = results.map(({ recordedDateTime, data }) => {
        const vitalRow = {
          recordedDateTime,
          // dynamically create null values
          ...Object.fromEntries(
            Object.keys(VITAL_MAP).flatMap((code) => [
              [code, null],
              [`${code}_STATUS`, null],
            ]),
          ),
        };

        data.forEach((v) => {
          const key = VITAL_MAP[v.vitalCode];
          if (!key) return;
          vitalRow[key] = v.value ?? null;
          vitalRow[`${key}_STATUS`] = v.vitalStatus ?? null;
        });

        return vitalRow;
      });

      setSavedVitalsList(finalVitalArray);
    } catch (err) {
      console.error(err);
    } finally {
      setSavedVitLoader(false);
    }
  };

  const fetchLatestVital = async () => {
    const payload = {
      patientId: selectedRpmPatient?.patientId,
      status: 1,
    };
    try {
      const res = await VitalService.getPatientRecentVital(payload);
      setLatestVitalData(res.data || []);
    } catch (e) {
      console.log(e);
    }
  };

  const vitalDisplayMap = {
    systolic: "BPS",
    diastolic: "BPD",
    height: "HI",
    weight: "WE",
    bmi: "BMI",
    temperature: "TP",
    spo2: "SPO2",
    heartRate: "HR",
    respiratoryRate: "RR",
  };

  const handleClosemodal = () => {
    setIsModalVisible(false);
  };

  const handleNewVitalEntry = () => {
    setIsModalVisible(true);
  };

  const getVitalValue = (key) => {
    switch (key) {
      case "heartRate":
        return latestVitalData?.find((item) => item.vitalCode === "HR")?.value;
      case "temperature":
        return latestVitalData?.find((item) => item.vitalCode === "TP")?.value;
      case "systolic":
      case "diastolic":
        const systolic = latestVitalData?.find(
          (item) => item.vitalCode === "BPS",
        )?.value;
        const diastolic = latestVitalData?.find(
          (item) => item.vitalCode === "BPD",
        )?.value;
        return systolic && diastolic
          ? `${systolic}/${diastolic}`
          : systolic || diastolic || "--";
      case "bmi":
        return latestVitalData?.find((item) => item.vitalCode === "BMI")?.value;
      case "height":
        return latestVitalData?.find((item) => item.vitalCode === "HI")?.value;
      case "weight":
        return latestVitalData?.find((item) => item.vitalCode === "WE")?.value;
      case "spo2":
        return latestVitalData?.find((item) => item.vitalCode === "SPO2")
          ?.value;
      case "respiratoryRate":
        return latestVitalData?.find((item) => item.vitalCode === "RR")?.value;
      default:
        return "--";
    }
  };

  const handleFetchUpdatedVital = () => {
    fetchPatientVitals(selectedMonth);
    fetchLatestVital();
  };

  const handleMonthChange = async (date) => {
    setSelectedMonth(date);
    await fetchPatientVitals(date);
  };

  return (
    <div className="mt-3">
      {/* Configured vital cards */}
      <div
        className="grid-cols gap-6 overflow-x-auto w-full"
        style={{ display: "-webkit-inline-box" }}
        id="unquie-scroll"
      >
        {(() => {
          const filteredVitals = Object.entries(rpmConfigVitalsList || {})
            .filter(([key, value]) => value === 1 && vitalDisplayMap[key]);

          const hasBP = filteredVitals.some(
            ([key]) => key === "systolic" || key === "diastolic",
          );
          const uniqueKeys = new Set(filteredVitals.map(([key]) => key));

          if (hasBP) {
            uniqueKeys.delete("systolic");
            uniqueKeys.delete("diastolic");
            uniqueKeys.add("bp");
          }

          if (uniqueKeys.size === 0) {
            return (
              <div className="flex flex-col items-center justify-center w-full">
                <p className="font-medium text-xl">No data found!!</p>
              </div>
            );
          }

          const orderedKeys = VITAL_ORDER.reduce((arr, code) => {
            if (code === "BP") {
              if (uniqueKeys.has("bp")) arr.push("bp");
            } else {
              const mapKey = Object.keys(vitalDisplayMap).find(
                (k) => vitalDisplayMap[k] === code,
              );
              if (mapKey && uniqueKeys.has(mapKey)) arr.push(mapKey);
            }
            return arr;
          }, []);
          uniqueKeys.forEach((k) => {
            if (!orderedKeys.includes(k)) orderedKeys.push(k);
          });

          return orderedKeys.map((key) => {
            let vitalCode, vitalData, vitalValue;

            if (key === "bp") {
              vitalCode = "BP";
              vitalData = vitalStyleArray.find(
                (item) => item.vitalCode === vitalCode,
              );
              vitalValue = getVitalValue("systolic");
            } else {
              vitalCode = vitalDisplayMap[key];
              vitalData = vitalStyleArray.find(
                (item) => item.vitalCode === vitalCode,
              );
              vitalValue = getVitalValue(key);
            }

            return (
              <div
                key={vitalCode + key}
                className="rounded-lg p-3 h-auto border-b-4"
                style={{
                  backgroundColor: vitalData?.backgroundColor,
                  width: "300px",
                  borderColor: vitalData?.color,
                }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-gray-700 mb-3 text-base font-medium text-left">
                      {vitalData?.title}
                    </div>
                    <div className="text-gray-800 mb-3 text-2xl font-semibold text-left">
                      {vitalValue} {vitalData?.unit}
                    </div>
                  </div>
                  <img
                    src={vitalData?.icon}
                    alt={vitalData?.title}
                    width={35}
                    className="mr-3"
                  />
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Activities */}
      {selectedRpmPatient?.rpmApplicable === "Y" &&
        selectedRpmPatient?.rpmSmartWatch === "Y" &&
        selectedRpmPatient?.smartWatchUserId && (
          <WatchData selectedRpmPatient={selectedRpmPatient} />
        )}

      {/* New and Filter */}
      <div className="md:flex justify-between items-center mt-5 py-3">
        <h1 className="font-bold text-xl">Vitals</h1>
        <div className="flex gap-4">
          <button
            onClick={handleNewVitalEntry}
            className="w-20 h-8 px-2 flex bg-[#F1FFF9] items-center gap-2 border border-[#3B98B9] rounded-md"
          >
            <CiCirclePlus className="w-5 h-5 text-[#189FAA]" />
            <span>New</span>
          </button>
          <DatePicker
            picker="month"
            defaultValue={selectedMonth}
            allowClear={false}
            onChange={handleMonthChange}
          />
        </div>
      </div>

      {/* Vital Table */}
      <div
        className={`${
          savedVitLoader === false && savedVitalsList?.length === 0
            ? "w-full flex justify-center items-center"
            : "overflow-y-auto h-[23rem]"
        }`}
      >
        {savedVitLoader && savedVitalsList?.length === 0 ? (
          <div className="flex items-center justify-center mt-[10%]">
            <Flex align="center" gap="middle">
              <Spin size="large" tip="Loading" />
            </Flex>
          </div>
        ) : savedVitalsList?.length === 0 ? (
          <div className="flex flex-col items-center justify-center">
            <img src={nodatafound} alt="nodatafound" />
            <p className="font-medium text-xl">No data found!!</p>
          </div>
        ) : (
          <ConfiguredVitalTable
            data={savedVitalsList}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalCount={savedVitalsList?.length}
            rpmConfigVitalsList={rpmConfigVitalsList}
            vitalDisplayMap={vitalDisplayMap}
          />
        )}
      </div>
      <hr className="mt-3" />

      <div>
        {savedVitalsList && (
          <VitalTrends
            data={savedVitalsList}
            rpmConfigVitalsList={rpmConfigVitalsList}
            vitalDisplayMap={vitalDisplayMap}
            selectedRpmPatient={selectedRpmPatient}
          />
        )}
      </div>
      <Modal
        title={<span className="font-bold text-base">Add Vitals</span>}
        open={isModalVisible}
        footer={[]}
        onCancel={handleClosemodal}
        width={800}
        destroyOnClose
      >
        <AddVitals
          onClose={handleClosemodal}
          selectedPatient={selectedRpmPatient}
          rpmConfigVitalsList={rpmConfigVitalsList}
          vitalDisplayMap={vitalDisplayMap}
          handleFetchUpdatedVital={handleFetchUpdatedVital}
        />
      </Modal>
    </div>
  );
}

export default Index;
